import React, { useState, useEffect } from 'react';
import { Card, Line } from 'ktm-ui';
import { MdEmail } from 'react-icons/md';
import EditCampaignModal from '../../components/Edit_campaign_modal';
import ReactMoment from 'react-moment';
import EmployeeCampaignTable from './Employee_campaigns';
import NewCampaignModal from '../../components/New_campaign_modal';
import { CustomLoading } from 'components/CustomLoading';
import axios from 'axios';
import toastr from 'toastr';
import {Button, Modal} from "react-bootstrap";
import ReactPlayer from 'react-player';
import { ExpandableNote } from 'components/ExpandableNote';
import moment from 'moment';

interface CampaignProps {
    policy_id: string;
    read_only: boolean;
}

const Campaign: React.FC<CampaignProps> = ({ policy_id, read_only }) => {
    const [loading, setLoading] = useState(true);
    const [hasCampaign, setHasCampaign] = useState(false);
    const [campaignData, setCampaignData] = useState({
        SelectionDeadline: null,
        LaunchDate: null,
        ReminderDate: null,
        LastDayReminder: null,
    });
    const [benefitEndDate, setBenefitEndDate] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showNewCampaignModal, setShowNewCampaignModal] = useState(false);
    const [acknowledgementPopUp, setAcknowledgementPopUp] = useState(false);
    const [hasAccrualEnabled, setHasAccrualEnabled] = useState(false);
    const [nextYearAccrualFrequency, setNextYearAccrualFrequency] = useState<string | null>(null);
    const [isStartingCampaign, setIsStartingCampaign] = useState(false);
    const [reopenModal, setReopenModal] = useState(false);
    const [isSavingCampaign, setIsSavingCampaign] = useState(false);
    const [reopenEditModal, setReopenEditModal] = useState(false);

    useEffect(() => {
        getCampaignInfo();
        getBenefitEndDate();
    }, [policy_id]);

    const getCampaignInfo = async () => {
        try {
            const [campaignRes, creditRes] = await Promise.all([
                fetch(`/api/Flex/getpolicycampaign?PolicyId=${policy_id}`),
                axios.get(`/api/Utility/GetScheduledCreditInfo?policyId=${policy_id}`)
            ]);

            const campaignDataJson = await campaignRes.json();

            if (campaignDataJson.success) {
                setCampaignData({
                    SelectionDeadline: campaignDataJson.campaign.selectionDeadline,
                    LaunchDate: campaignDataJson.campaign.launchDate,
                    ReminderDate: campaignDataJson.campaign.reminderDate,
                    LastDayReminder: campaignDataJson.campaign.lastDayReminder,
                });
                setHasCampaign(true);
            } else {
                setHasCampaign(false);
            }

            if (creditRes.data.success) {
                setHasAccrualEnabled(creditRes.data.hasAccrualEnabled);
                setNextYearAccrualFrequency(creditRes.data.nextYearAccrualFrequency);
            } else {
                toastr.error(creditRes.data.message, 'Error');
            }
        } catch (error) {
            toastr.error('Error getting campaign or scheduled credit info', 'Error');
            setHasCampaign(false);
        } finally {
            setLoading(false);
        }
    };

    const getBenefitEndDate = async () => {
        try {
            const response = await axios.get(`/api/Flex/getbenefitenddate?policyId=${policy_id}`);
            if (response.data.success) {
                setBenefitEndDate(response.data.currentBenYearEndDate.endDate);
            } else {
                toastr.error(response.data.message, 'Error');
            }
        } catch (error) {
            toastr.error('Failed to get benefit end date', 'Error');
        }
    };

    const handleProceedToNewCampaign = () => {
        setAcknowledgementPopUp(false);
        setShowNewCampaignModal(true);
    };

    if (loading) {
        return <CustomLoading />;
    }

    const renderCampaignDetails = () => (
        <div className="campaign-info-container">
            <EditCampaignModal
                showEditCampaignModal={showEditModal || reopenEditModal}
                closeModal={() => {
                    setShowEditModal(false);
                    setReopenEditModal(false);
                }}
                refreshCampaigns={() => {
                    setShowEditModal(false);
                    getCampaignInfo();
                }}
                policy_id={policy_id}
                SelectionDeadline={campaignData.SelectionDeadline}
                LaunchDate={campaignData.LaunchDate}
                ReminderDate={campaignData.ReminderDate}
                LastDayReminder={campaignData.LastDayReminder}
                benefitEndDate={benefitEndDate}
                hasAccrualEnabled={hasAccrualEnabled}
                nextYearAccrualFrequency={nextYearAccrualFrequency}
                IsPlanAdmin={true}
                setParentLoading={setIsSavingCampaign}
                onFailedSave={() => setReopenEditModal(true)}
            />
            <div className="RT-Section">
                <div className="RT-Section__header">
                    <div className="RT-Section__title" style={{fontSize:16, fontStyle: "bold"}}>
                        Flex Campaign Details
                    </div>
                    {!read_only && <div className="RT-Section__actions">
                        <a href="#" className="RT-Link" onClick={() => setShowEditModal(true)}>
                            Edit
                        </a>
                    </div>}
                </div>
                <Line>
                {['LaunchDate', 'ReminderDate', 'LastDayReminder', 'SelectionDeadline'].map((dateType) => (
                    <div key={dateType} className="RT-Static-Field">
                        <label className="RT-Static-Field__label">{`${dateType.replace(/([A-Z])/g, ' $1')} (YYYY-MM-DD)`}</label>
                        <div className="RT-Static-Field__content" style={{ height: '21px' }}>
                            {campaignData[dateType] ? <ReactMoment format="YYYY-MM-DD">{campaignData[dateType]}</ReactMoment> : ''}
                        </div>
                    </div>
                ))}
                {hasAccrualEnabled ? (
                    <div className="RT-Static-Field">
                        <label className="RT-Static-Field__label">Scheduled Credits Frequency</label>
                        <div className="RT-Static-Field__content" style={{ height: '21px' }}>
                            {nextYearAccrualFrequency ? nextYearAccrualFrequency : 'None'}
                        </div>
                    </div>
                ) : <></>}
                </Line>

                <ExpandableNote title="Welcome to your Year-End Flex Campaign!" defaultExpanded={true}>
                    <p style={{ marginTop: "10px", fontSize: "14.2px", lineHeight: "1.6" }}>
                        Flex Selection emails are scheduled to go out automatically according to the first three dates chosen above.
                        Please take a moment to review the employee list and Flex amounts below for the upcoming benefit
                        year. Once reviewed, there is nothing left to do.
                        <br />
                        If adjustments are required, any updates made within the Campaign tab apply ONLY to the next benefit year, never the current one.
                        <br />
                        You also have the ability to send Flex Selection emails manually to all or to selected
                        employees, at any time after the Launch date, with our <strong>Send Manual Email(s)</strong> button!
                        This does not impact the automatically scheduled emailing.
                        <br />
                        If necessary, use the <strong>Toggle Email(s)</strong> button to enable
                        or disable selected employees from receiving scheduled email notifications.
                        <br />
                        Don’t hesitate to contact{" "}
                        <a
                        href="mailto:enrollments@nationalhealthclaim.com"
                        style={{ color: "#4987ef" }}
                        >
                        enrollments@nationalhealthclaim.com
                        </a>{" "}
                        if you require assistance!
                        <br />
                        <a
                        href="https://nhcfilestorage.blob.core.windows.net/docfiles/Flex_Campaign_Help_Page.pdf"
                        target="_blank"
                        style={{ fontStyle: "italic", color: "#4987ef" }}
                        >
                        Click here for help with campaign features.
                        </a>
                    </p>
                </ExpandableNote>

                {nextYearAccrualFrequency && (
                <ExpandableNote title="Scheduled Credits Information" defaultExpanded={true}>
                    <p>
                        Scheduled Credits will be released to employees on a{" "}
                        <strong>{nextYearAccrualFrequency}</strong> basis in the upcoming year
                        according to category, and selection emails will guide employees accordingly.
                        <br />
                        If a change to Scheduled Credits or to the policy’s Frequency is required,
                        you must contact{" "}
                        <a
                        href="mailto:enrollments@nationalhealthclaim.com"
                        style={{ color: "#4987ef", fontWeight: 500 }}
                        >
                        enrollments@nationalhealthclaim.com
                        </a>{" "}
                        before the campaign <strong>Launch date</strong> at the top of the page.
                    </p>
                </ExpandableNote>
                )}
            </div>
            <div className="employee-campaigns-container" style={{ marginTop: '15px' }}>
                <EmployeeCampaignTable
                    policy_id={policy_id}
                    disableBtn={!campaignData.SelectionDeadline}
                    benefitEndDate={benefitEndDate}
                    selectionDeadline={campaignData.SelectionDeadline}
                    launchDate={campaignData.LaunchDate}
                    HasAccrual={!!nextYearAccrualFrequency}
                    readOnly={read_only}
                />
            </div>
        </div>
    );

    const renderEmptyState = () => {
        const currentDate = moment();
        const canAddCampaign = benefitEndDate && moment(benefitEndDate).add(23, 'hours').add(59, 'minutes').add(59, 'seconds').diff(currentDate, 'months') <= 1;
    
        return (
            <div className='campaign-empty-state' style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 60, textAlign: 'center' }}>
                <MdEmail style={{ fill: 'rgb(100,120,140)', fontSize: 80, opacity: 0.2 }} />
                <div style={{ fontSize: '15px', fontWeight: 500 }}>
                    The next benefit year’s Flex Selection Campaign can be created up to 2 months before your current benefit year-end date.
                    We will provide emailed notifications with instruction at that time.
                    <br /><br/>
                    If no Flex Campaign is manually created within 1 month of your current benefit year-end date, a Flex Campaign will automatically generate on your behalf.
                    <br />
                    We will also then email a reminder to Plan Administrators to review the next year’s employee list and Flex totals before emails are launched to employees.
                    <br/>
                    For further assistance, please reach out to <a style={{"color":"#0d6efd"}} href="mailto:enrollments@nationalhealthclaim.com">enrollments@nationalhealthclaim.com</a>
                    <br/><br/>
                    Please Note: Creating a new Flex Campaign always applies to the next benefit year, never the current one.
                </div>
                <ReactPlayer
                    url="https://player.vimeo.com/video/1020310923?h=70c621da36&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479" 
                    width="700px"
                    height="500px"
                    controls={true}
                />
                <button
                    className="RT-Button RT-Button--primary"
                    onClick={() => setAcknowledgementPopUp(true)}
                    style={{ width: 200, height: 40, fontSize: 17 }}
                    disabled={!canAddCampaign && read_only}
                    >
                    Add Flex Campaign
                </button>
                <AcknowledgementModal
                    show={acknowledgementPopUp}
                    onClose={() => setAcknowledgementPopUp(false)}
                    onProceed={handleProceedToNewCampaign}
                />
                <NewCampaignModal
                    policy_id={policy_id}
                    showNewCampaignModal={showNewCampaignModal || reopenModal}
                    closeModal={() => {
                        setShowNewCampaignModal(false);
                        setReopenModal(false);
                    }}
                    refreshCampaigns={() => {
                        setShowNewCampaignModal(false);
                        getCampaignInfo();
                    }}
                    setParentLoading={setIsStartingCampaign}
                    onFailedStart={() => setReopenModal(true)}
                    benefitEndDate={benefitEndDate}
                    accrualFrequency={nextYearAccrualFrequency}
                    IsPlanAdmin={true}
                />
            </div>
        );
    };

    return (
        <div>
            {isStartingCampaign ? <CustomLoading /> : (hasCampaign ? renderCampaignDetails() : renderEmptyState())}
        </div>
    );
};

export default Campaign;

interface AcknowledgementModalProps {
    show: boolean;
    onClose: () => void;
    onProceed: () => void;
}

const AcknowledgementModal: React.FC<AcknowledgementModalProps> = ({ show, onClose, onProceed }) => {
    return (
        <Modal className="react-modal edit-campaign-modal" show={show}>
            <Modal.Header>
                <Modal.Title>Helpful Tip!</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Card>
                    <p>
                        Before you proceed to the next step, we highly recommend a quick review of your current year first. 
                        It is not required in order to continue, but may save you time within the Flex Campaign tab if adjustments 
                        to employees' <strong>current</strong> year are necessary.
                        <strong> If a new Flex category amount will be required for use only in the next benefit year,</strong> you may still proceed 
                        to the next steps, but a new category will need to be created. Please reach out to <a href="mailto:enrollments@nationalhealthclaim.com" style={{"color":"#0d6efd"}}>enrollments@nationalhealthclaim.com</a> for assistance.
                    </p>
                </Card>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={onProceed}>
                    Proceed
                </Button>
            </Modal.Footer>
        </Modal>
    );
};